import { Button } from '@petsmart-ui/sparky';
import cn from 'classnames';
import {
    Children,
    type MouseEvent,
    type ReactElement,
    type TouchEvent,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';

import { useDeviceType } from '@libs/common-next';

import './HorizontalCarousel.scss';

const defaultBreakpoints: Record<number, { perView: number; gap: number }> = {
    1200: {
        perView: 6,
        gap: 64,
    },
    1080: {
        perView: 5,
        gap: 64,
    },
    960: {
        perView: 5,
        gap: 64,
    },
    768: {
        perView: 4,
        gap: 16,
    },
    560: {
        perView: 3,
        gap: 16,
    },
    375: {
        perView: 3,
        gap: 16,
    },
};

type CarouselProps = {
    className: string;
    children: ReactElement[];
    onLeftArrowClick: () => void;
    onRightArrowClick: () => void;
    onDragging: (x: boolean) => void;
    perViewDefault: number;
    breakpoints?: Record<number, { perView: number; gap: number }>;
};

export function HorizontalCarousel({
    children,
    className,
    perViewDefault = 6,
    onLeftArrowClick,
    onRightArrowClick,
    breakpoints = defaultBreakpoints,
    onDragging,
}: CarouselProps) {
    const carouselRef = useRef<HTMLDivElement>(null);
    const [isMouseDown, setIsMouseDown] = useState(false);
    const [startX, setStartX] = useState(0);
    const [currentTranslate, setCurrentTranslate] = useState(0);
    const [prevTranslate, setPrevTranslate] = useState(0);
    const [showNext, setShowNext] = useState(false);
    const [showPrev, setShowPrev] = useState(false);
    const [itemWidth, setItemWidth] = useState<number>();
    const [breakpoint, setBreakpoint] = useState({
        perView: perViewDefault,
        gap: 64,
    });

    const { isMobile } = useDeviceType();

    useEffect(() => {
        if (!window.matchMedia) {
            return;
        }

        const breakpointQueries = Object.entries(breakpoints).map(([point, matchingBreakpoint]) => ({
            matchingBreakpoint,
            queryList: window.matchMedia(`(max-width: ${point}px)`),
        }));

        const updateBreakpoint = () => {
            const matchingQueries = breakpointQueries.filter(({ queryList }) => queryList.matches);

            if (matchingQueries.length === 0) {
                setBreakpoint({
                    perView: perViewDefault,
                    gap: 64,
                });
                return;
            }

            setBreakpoint(currentBreakpoint => {
                let nextBreakpoint = currentBreakpoint;

                matchingQueries.forEach(({ matchingBreakpoint }) => {
                    if (matchingBreakpoint.perView !== currentBreakpoint.perView) {
                        nextBreakpoint = matchingBreakpoint;
                    }
                });

                return nextBreakpoint;
            });
        };

        updateBreakpoint();

        breakpointQueries.forEach(({ queryList }) => {
            queryList.addEventListener('change', updateBreakpoint);
        });

        return () => {
            breakpointQueries.forEach(({ queryList }) => {
                queryList.removeEventListener('change', updateBreakpoint);
            });
        };
    }, [breakpoints, perViewDefault]);

    useEffect(() => {
        const carousel = carouselRef.current;

        if (!carousel) {
            return;
        }

        const updateItemWidth = () => {
            setItemWidth(carousel.clientWidth / breakpoint.perView);
        };

        updateItemWidth();

        const observer = new ResizeObserver(updateItemWidth);
        observer.observe(carousel);

        return () => {
            observer.disconnect();
        };
    }, [breakpoint.perView, isMobile]);

    const updateButtonVisibility = useCallback(() => {
        if (carouselRef?.current) {
            const { scrollWidth, clientWidth } = carouselRef.current;
            setShowNext(scrollWidth - clientWidth - -currentTranslate > 0);
            setShowPrev(currentTranslate < 0);
        }
    }, [currentTranslate]);

    useEffect(() => {
        updateButtonVisibility();
    }, [updateButtonVisibility, prevTranslate, carouselRef?.current?.scrollWidth]);

    const handleMouseDown = (e: MouseEvent<HTMLImageElement>) => {
        e.preventDefault();
        setIsMouseDown(true);
        setStartX(e.pageX);
    };

    const handleMouseUp = () => {
        if (carouselRef.current) {
            setIsMouseDown(false);
            let calcTranslate = currentTranslate;

            const { scrollWidth, clientWidth } = carouselRef.current;
            if (scrollWidth - clientWidth - -currentTranslate < 0) {
                calcTranslate = clientWidth - scrollWidth;
                setCurrentTranslate(clientWidth - scrollWidth);
            }

            if (currentTranslate === prevTranslate) {
                onDragging(false);
            }

            if (currentTranslate > 0) {
                calcTranslate = 0;
                setCurrentTranslate(0);
            }
            setPrevTranslate(calcTranslate);
        }
    };

    const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
        if (isMouseDown) {
            const x = e.pageX;
            const walk = x - startX;
            if (Math.abs(e.clientX - startX) > 10) {
                onDragging(true);
                setCurrentTranslate(prevTranslate + walk);
            }
        }
    };

    const handleTouchStart = (e: TouchEvent<HTMLDivElement>) => {
        setIsMouseDown(true);
        onDragging(true);
        setStartX(e.touches[0].pageX);
    };

    const handleTouchMove = (e: TouchEvent<HTMLDivElement>) => {
        if (isMouseDown) {
            const x = e.touches[0].pageX;
            const walk = x - startX;
            setCurrentTranslate(prevTranslate + walk);
        }
    };

    const handleEndEvent = () => {
        if (isMouseDown) {
            handleMouseUp();
        }
    };

    const handleNext = () => {
        if (carouselRef.current) {
            const { scrollWidth, clientWidth } = carouselRef.current;
            setCurrentTranslate(clientWidth - scrollWidth);
            setPrevTranslate(clientWidth - scrollWidth);
            onRightArrowClick();
        }
    };

    const handlePrev = () => {
        setCurrentTranslate(0);
        setPrevTranslate(0);
        onLeftArrowClick();
    };

    const componentClassName = cn('carousel-container', className);

    return (
        <div aria-label="Image carousel" className={componentClassName}>
            {isMobile ? (
                <div className="carousel-mobile" role="listbox">
                    {Children.map(children, (child, index) => (
                        <div className="carousel-outer-item" key={child?.key || index}>
                            {child}
                        </div>
                    ))}
                </div>
            ) : (
                <>
                    <div
                        id="swipe-carousel"
                        role="listbox"
                        className="carousel"
                        tabIndex={0}
                        ref={carouselRef}
                        onMouseDown={handleMouseDown}
                        onMouseLeave={handleEndEvent}
                        onMouseUp={handleMouseUp}
                        onMouseMove={handleMouseMove}
                        onTouchStart={handleTouchStart}
                        onTouchEnd={handleEndEvent}
                        onTouchMove={handleTouchMove}
                        style={{
                            transform: `translate3d(${currentTranslate}px, 0, 0)`,
                            transition: isMouseDown ? 'none' : 'transform 800ms cubic-bezier(0.165, 0.84, 0.44, 1)', // Smooth transition when not dragging
                        }}
                    >
                        <div className="carousel-inner">
                            {Children.map(children, (child, index) => (
                                <div
                                    className="carousel-outer-item"
                                    style={{
                                        gap: `${breakpoint?.gap}px`,
                                        minWidth: itemWidth ? `${itemWidth}px` : undefined,
                                    }}
                                    key={child?.key || index}
                                >
                                    {child}
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="carousel-arrow-container">
                        {showPrev && (
                            <Button
                                circle
                                text="arrow-prev"
                                aria-label="Previous slide"
                                iconName="chevron-left"
                                iconPosition="before"
                                iconSize="md"
                                hideText
                                variant="primary"
                                className="carousel-arrows carousel-arrow-left"
                                data-testid="carousel-arrow-left"
                                onClick={handlePrev}
                            />
                        )}
                        {showNext && (
                            <Button
                                circle
                                text="arrow-next"
                                aria-label="Next slide"
                                iconName="chevron-right"
                                iconPosition="before"
                                iconSize="md"
                                hideText
                                variant="primary"
                                className="carousel-arrows carousel-arrow-right"
                                data-testid="carousel-arrow-right"
                                onClick={handleNext}
                            />
                        )}
                    </div>
                </>
            )}
        </div>
    );
}
